import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronRight } from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';

const Th = ({ children, align = 'left' }) => (
    <th style={{
        padding: '10px 14px', fontSize: 11, fontWeight: 700,
        color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.4px',
        textAlign: align, backgroundColor: '#f9fafb',
        borderBottom: '1px solid #f3f4f6', whiteSpace: 'nowrap',
    }}>
        {children}
    </th>
);

const Cell = ({ children, align = 'left', muted, color }) => (
    <td style={{
        padding: '10px 14px', fontSize: 13,
        color: color || (muted ? '#9ca3af' : '#374151'),
        fontWeight: muted ? 400 : 500, textAlign: align,
        borderBottom: '1px solid #f3f4f6', whiteSpace: 'nowrap',
    }}>
        {children ?? '—'}
    </td>
);

const fmt = (n) => (n != null ? Number(n).toLocaleString() : '—');
const fmtKg = (n) => (n != null ? `${fmt(n)} kg` : '—');

const EmptyState = ({ message }) => (
    <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af', fontSize: 13 }}>{message}</div>
);

const cardStyle = {
    backgroundColor: '#fff', borderRadius: 16,
    border: '1px solid #f3f4f6',
    boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
    overflow: 'hidden',
};

const MovementProductsTable = ({ items = [], loading, total, onRowClick }) => {
    const { t } = useTranslation();
    return (
        <div style={cardStyle}>
            <div style={{ padding: '14px 18px', borderBottom: '1px solid #f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                    <div style={{ fontSize: 15, fontWeight: 700, color: '#111827' }}>{t('movementAnalytics.headings.productMovement')}</div>
                    <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 2 }}>{t('movementAnalytics.subtitles.purchaseVsSalesPerProduct')}</div>
                </div>
                {items.length > 0 && (
                    <span style={{ fontSize: 11, fontWeight: 700, color: '#374151', backgroundColor: '#f3f4f6', borderRadius: 20, padding: '3px 10px' }}>
                        {total ?? items.length} {t('movementAnalytics.labels.productsSuffix')}
                    </span>
                )}
            </div>

            {loading && <EmptyState message={t('movementAnalytics.emptyStates.loadingProductMovement')} />}
            {!loading && !items.length && <EmptyState message={t('movementAnalytics.emptyStates.noProductMovement')} />}

            {!loading && items.length > 0 && (
                <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 900 }}>
                        <thead>
                            <tr>
                                <Th>{t('movementAnalytics.columns.rank')}</Th>
                                <Th>{t('movementAnalytics.columns.product')}</Th>
                                <Th align="right">{t('movementAnalytics.columns.purchaseKg')}</Th>
                                <Th align="right">{t('movementAnalytics.columns.salesKg')}</Th>
                                <Th align="right">{t('movementAnalytics.columns.netKg')}</Th>
                                <Th align="center" />
                            </tr>
                        </thead>
                        <tbody>
                            {items.map((row, i) => {
                                const net = row.net_weight_kg ?? 0;
                                const netColor = net >= 0 ? '#059669' : '#d97706';
                                return (
                                    <tr
                                        key={row.product_id ?? i}
                                        onClick={() => onRowClick?.(row)}
                                        style={{ backgroundColor: i % 2 === 0 ? '#ffffff' : '#fafafa', cursor: 'pointer' }}
                                        onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#f0f9ff')}
                                        onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = i % 2 === 0 ? '#ffffff' : '#fafafa')}
                                    >
                                        <Cell muted>{i + 1}</Cell>
                                        <Cell><span style={{ fontWeight: 700, color: '#111827' }}>{row.product_name}</span></Cell>
                                        <Cell align="right" color="#059669">
                                            {fmtKg(row.purchase?.weight_kg)}
                                            <span style={{ color: '#9ca3af', fontWeight: 400, marginLeft: 4 }}>
                                                ({fmt(row.purchase?.challan_count)})
                                            </span>
                                        </Cell>
                                        <Cell align="right" color="#ef4444">
                                            {fmtKg(row.sales?.weight_kg)}
                                            <span style={{ color: '#9ca3af', fontWeight: 400, marginLeft: 4 }}>
                                                ({fmt(row.sales?.challan_count)})
                                            </span>
                                        </Cell>
                                        <Cell align="right" color={netColor}>
                                            <span style={{ fontWeight: 700 }}>{net >= 0 ? '+' : ''}{fmt(net)} kg</span>
                                        </Cell>
                                        <Cell align="center">
                                            <FontAwesomeIcon icon={faChevronRight} style={{ color: '#cbd5e1', fontSize: 12 }} />
                                        </Cell>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

export default MovementProductsTable;
